import { ACTION, IconName, useDisclosureWithContext } from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useRoleAuth } from '../../../hooks';
import type { UpdatePositionPrimer } from '../PositionsV3/UpdatePositionsDialog';
import { AdjustMarketAccountPositionsDialog } from './AdjustMarketAccountPositionsDialog';
import { useUpdateBalancesAccounts } from './useUpdateBalancesAccounts';

interface UseUpdateBalancesDialogParams<T> {
  /** When false, Adjust dialog does not mount and menu/open handlers are no-ops (legacy path active). */
  enabled?: boolean;
  getMarketAccount: (item: T) => string | undefined;
  getAsset: (item: T) => string | undefined;
  getAvgCost?: (item: T) => string | undefined;
  getAvgCostCurrency?: (item: T) => string | undefined;
  /**
   * If you want the user to be able to right-click the market account grouping row in the blotter and be able to open the dialog
   * for that market account, then provide the field the blotter is using to get the market account off the entity. This will
   * enable this functionality.
   */
  blotterMarketAccountField?: string;
  /**
   * When false, the context menu item shows for any position with a market account when the user has EDIT_POSITION,
   * even if the org has no BalancePublish config or the market account is not in the BalancePublish list.
   * Use this for Positions blotter to restore visibility that matched the old "Edit position" flow.
   * Default true (Balances blotter): requires BalancePublish config and market account in list.
   */
  requireBalancePublishForContextMenu?: boolean;
}

/**
 * Collects and prepares the components, state and callbacks you need to use the Adjust Market Account Position dialog in your blotter.
 * This dialog creates AssetTransactions via POST /asset-transactions. It is only applicable for certain accounts as
 * specified by the OrgConfiguration "portfolio.BalancePublishMarketAccountNames".
 */
export const useUpdateBalancesDialog = <T,>({
  enabled = true,
  getMarketAccount,
  getAsset,
  getAvgCost,
  getAvgCostCurrency,
  blotterMarketAccountField,
  requireBalancePublishForContextMenu = true,
}: UseUpdateBalancesDialogParams<T>) => {
  const { isAuthorized } = useRoleAuth();
  const disclosure = useDisclosureWithContext<UpdatePositionPrimer>();
  const { accounts: updatableAccounts, accountsSet: updatableAccountsSet } = useUpdateBalancesAccounts();
  const authorizedToEdit = isAuthorized(ACTION.EDIT_POSITION);
  const hasNonEmptyAllowlist = (updatableAccounts?.length ?? 0) > 0;
  const show =
    enabled &&
    authorizedToEdit &&
    (requireBalancePublishForContextMenu ? hasNonEmptyAllowlist : updatableAccounts != null);
  const showContextMenuItem = enabled && (requireBalancePublishForContextMenu ? show : authorizedToEdit);

  const dialogComponent = useMemo(() => {
    if (!enabled || !showContextMenuItem) {
      return null;
    }

    return (
      <AdjustMarketAccountPositionsDialog
        disclosure={disclosure}
        customMarketAccountOptions={requireBalancePublishForContextMenu ? updatableAccounts : undefined}
      />
    );
  }, [enabled, showContextMenuItem, disclosure, updatableAccounts, requireBalancePublishForContextMenu]);

  const getContextMenuItem = useCallback(
    (params: GetContextMenuItemsParams<T>): MenuItemDef | undefined => {
      if (!enabled || !showContextMenuItem) {
        return undefined;
      }

      const data = params.node?.data;
      let mktAcc: string | undefined = undefined;
      let asset: string | undefined = undefined;

      if (data) {
        mktAcc = getMarketAccount(data);
        asset = getAsset(data);
      } else if (blotterMarketAccountField) {
        const isMarketAccountGroupNode = params.node?.field === blotterMarketAccountField;
        if (isMarketAccountGroupNode) {
          mktAcc = params.node?.key ?? undefined;
        }
      }

      if (!mktAcc) {
        return undefined;
      }

      if (requireBalancePublishForContextMenu && !updatableAccountsSet?.has(mktAcc)) {
        return undefined;
      }

      return {
        name: asset ? 'Adjust Market Account Position' : 'Adjust Market Account Positions',
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        action: () => {
          disclosure.open({
            MarketAccount: mktAcc,
            Asset: asset,
            AvgCost: data ? getAvgCost?.(data) : undefined,
            AvgCostCurrency: data ? getAvgCostCurrency?.(data) : undefined,
          });
        },
      };
    },
    [
      enabled,
      showContextMenuItem,
      blotterMarketAccountField,
      updatableAccountsSet,
      requireBalancePublishForContextMenu,
      getMarketAccount,
      getAsset,
      getAvgCost,
      getAvgCostCurrency,
      disclosure,
    ]
  );

  const openUpdateBalancesDialog = useCallback(() => {
    if (!enabled) {
      return;
    }
    disclosure.open({});
  }, [enabled, disclosure]);

  return {
    updateBalancesDialogComponent: dialogComponent,
    getUpdateBalanceContextMenuItem: getContextMenuItem,
    openUpdateBalancesDialog,
    showUpdateBalancesButton: show,
  };
};
